import * as React from 'react';
import { Select } from 'antd';
import { NiInput, NiSelect } from '@component/index';
import { countries, ICountry } from './source';
import '@style/component/country-input/index.less';

const {
  useCallback,
  useState,
  useMemo,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle
} = React;

interface PropTypes {
  locale?: string;
  onBlur?: (e: any) => void;
  onPressEnter?: (e: any) => void;
  onChange?: Function;
  value?: CountryPhoneCodeValue;
  inputProps?: any;
  style?: React.CSSProperties;
}

interface CountryPhoneCodeValue {
  code?: number;
  phone?: string;
  short?: string;
}

// 接收两个参数：props 和 ref
const CountryPhoneCode = (
  { locale, onChange, onBlur, onPressEnter, value, inputProps, style }: PropTypes,
  ref: React.Ref<any>
) => {
  // 根据 window.location.pathname 决定默认国家
  const defaultCountry: ICountry | undefined = useMemo(() => {
    return countries.find(c =>
      c.short === (window.location.pathname.endsWith('copilot_login') ? 'US' : 'CN')
    );
  }, []);

  const [country, setCountry] = useState<ICountry | undefined>(defaultCountry);
  const [phone, setPhone] = useState<string | undefined>();

  // 使用局部 ref 来引用 NiInput
  const localPhoneRef = useRef<any>(null);

  // 将父组件传入的 ref 指向 localPhoneRef.current
  useImperativeHandle(ref, () => localPhoneRef.current);

  // 当 value 发生变化时，同步更新 country 和 phone
  useEffect(() => {
    if (value !== undefined) {
      if (value.short) {
        setCountry(countries.find(c => c.short === value.short));
      } else {
        setCountry(countries.find(c => Number(c.phoneCode) === value.code));
      }
      setPhone(value.phone);
    }
  }, [value]);

  const triggerChange = useCallback(
    (phone?: string, country?: ICountry) => {
      const result: CountryPhoneCodeValue = {
        phone,
        code: country && Number(country.phoneCode),
        short: country && country.short
      };
      if (onChange) {
        onChange(result);
      }
    },
    [onChange]
  );

  const handleCountryChange = useCallback(
    (value: string) => {
      const c = countries.find(c => c.short === value);
      if (!c) {
        throw new Error(`Country not found: ${value}`);
      }
      setCountry(c);
      triggerChange(phone, c);
    },
    [triggerChange, phone]
  );

  const handlePhoneChange = useCallback(() => {
    // @ts-ignore: 防止 phoneRef.current 为空的情况
    const inputValue = localPhoneRef.current?.input?.value;
    setPhone(inputValue);
    triggerChange(inputValue, country);
  }, [country, triggerChange]);

  return (
    <NiInput
      style={{ ...style }}
      ref={localPhoneRef}
      onChange={handlePhoneChange}
      addonBefore={
        <Select
          bordered={false}
          value={country && country.short}
          style={{ width: '100px' }}
          dropdownMatchSelectWidth={false}
          optionLabelProp="label"
          onChange={handleCountryChange}
        >
          {countries.map((item: any) => {
            const fix = {
              key: item.short,
              value: item.short,
              label: `${item.emoji} +${item.phoneCode}`
            };
            return (
              <NiSelect.Option {...fix}>
                {item.emoji} {(item as any)[locale || 'en']} {item.phoneCode}
              </NiSelect.Option>
            );
          })}
        </Select>
      }
      onBlur={() => {
        if (onBlur) {
          onBlur({
            phone,
            code: country && Number(country.phoneCode),
            short: country && country.short
          });
        }
      }}
      onPressEnter={() => {
        if (onPressEnter) {
          onPressEnter({
            phone,
            code: country && Number(country.phoneCode),
            short: country && country.short
          });
        }
      }}
      value={value && value.phone}
      {...inputProps}
    />
  );
};

// 使用 forwardRef 包装组件，确保组件接受两个参数（props 和 ref）
export default forwardRef(CountryPhoneCode);
